import { Box, Center, Divider, Text, VStack } from '@chakra-ui/react'
import { Alert, colors, getDateString, getSortedKeys } from '@hb/shared'
import { FC, useMemo, useState } from 'react'
import { useProfile } from '../../../contexts/user/ProfileContext'
import { SolidActionButton } from '../../Buttons'

export const UrgentAlertsPopUp = () => {
  const {
    user,
    selectedAssessment: { populated: selectedAssessment },
  } = useProfile()
  const {
    adminAlerts: adminAssessmentAlerts,
    miscarried,
    practiceAlerts: practiceAssessmentAlerts,
  } = selectedAssessment ?? {}
  const { adminAlerts: adminPatientAlerts, practiceAlerts: practicePatientAlerts } = user ?? {}

  const normalizedAdminPatientAlerts = useMemo(
    () =>
      Object.entries(adminPatientAlerts ?? {}).reduce<Record<string, Alert & { admin?: boolean }>>(
        (acc, [key, value]) => {
          acc[key] = {
            ...(typeof value === 'string' ? { text: value } : value),
            admin: true,
          }
          return acc
        },
        {},
      ),
    [adminPatientAlerts],
  )

  const normalizedPracticePatientAlerts = useMemo(
    () =>
      Object.entries(practicePatientAlerts ?? {}).reduce<
        Record<string, Alert & { practice?: boolean }>
      >((acc, [key, value]) => {
        acc[key] = {
          ...(typeof value === 'string' ? { text: value } : value),
          practice: true,
        }
        return acc
      }, {}),
    [practicePatientAlerts],
  )

  const patientAlerts = useMemo<Record<string, Alert & { practice?: boolean; admin?: boolean }>>(
    () => ({
      ...normalizedAdminPatientAlerts,
      ...normalizedPracticePatientAlerts,
    }),
    [normalizedAdminPatientAlerts, normalizedPracticePatientAlerts],
  )

  const normalizedAdminAssessmentAlerts = useMemo(
    () =>
      Object.entries(adminAssessmentAlerts ?? {}).reduce<
        Record<string, Alert & { admin?: boolean }>
      >((acc, [key, value]) => {
        acc[key] = {
          ...(typeof value === 'string' ? { text: value } : value),
          admin: true,
        }
        return acc
      }, {}),
    [adminAssessmentAlerts],
  )

  const normalizedPracticeAssessmentAlerts = useMemo(
    () =>
      Object.entries(practiceAssessmentAlerts ?? {}).reduce<
        Record<string, Alert & { practice?: boolean }>
      >((acc, [key, value]) => {
        acc[key] = {
          ...(typeof value === 'string' ? { text: value } : value),
          practice: true,
        }
        return acc
      }, {}),
    [practiceAssessmentAlerts],
  )

  const assessmentAlerts = useMemo<Record<string, Alert & { practice?: boolean; admin?: boolean }>>(
    () => ({
      ...normalizedAdminAssessmentAlerts,
      ...normalizedPracticeAssessmentAlerts,
    }),
    [normalizedAdminAssessmentAlerts, normalizedPracticeAssessmentAlerts],
  )
  const patientAlertKeys = useMemo(
    () => getSortedKeys(patientAlerts).map(k => parseInt(k, 10)),
    [patientAlerts],
  )

  const assessmentAlertKeys = useMemo(
    () => getSortedKeys(assessmentAlerts).map(k => parseInt(k, 10)),
    [assessmentAlerts],
  )
  const urgentAlerts = useMemo<
    Array<{
      text: string
      practice?: boolean
      admin?: boolean
      Render?: FC<{ closeAlertsPopup: () => void }>
    }>
  >(() => {
    const alerts = []
    if (miscarried) {
      alerts.push({ text: 'Assessment: Miscarried' })
    }

    if (
      selectedAssessment?.insurerCallInSubmittedOn &&
      !selectedAssessment.insurerCallInAlertDismissedOn
    ) {
      alerts.push({
        text: 'Patient updated questions for insurer call-in, HB to review assessment/finalize',
        admin: true,
        // Render: DismissCallInAlert,
      })
    }

    const inquiryOrAuthList = selectedAssessment?.adminStages.find(
      s => s === 'Inquiry' || s === 'Authorization',
    )
    if (inquiryOrAuthList) {
      if (selectedAssessment?.delivery?.isTransfer) {
        alerts.push({
          text: `Delivery: Transfer and on ${inquiryOrAuthList} list`,
        })
      } else if (selectedAssessment?.deliveredOn) {
        alerts.push({
          text: `Delivered on ${getDateString(selectedAssessment?.deliveredOn)} and on ${inquiryOrAuthList} list`,
        })
      }
    }
    if (patientAlertKeys.length) {
      patientAlertKeys.forEach(k => {
        const alert = patientAlerts?.[k]
        if (typeof alert !== 'string' && alert?.urgent) {
          alerts.push({
            text: `Patient: ${alert.text}`,
            practice: alert.practice,
            admin: alert.admin,
          })
        }
      })
    }
    if (assessmentAlertKeys.length) {
      assessmentAlertKeys.forEach(k => {
        const alert = assessmentAlerts?.[k]
        if (typeof alert !== 'string' && alert?.urgent) {
          alerts.push({
            text: `Assessment: ${alert.text}`,
            practice: alert.practice,
            admin: alert.admin,
          })
        }
      })
    }
    return alerts
  }, [
    assessmentAlertKeys,
    miscarried,
    patientAlertKeys,
    patientAlerts,
    assessmentAlerts,
    selectedAssessment,
  ])
  const [closed, setClosed] = useState(urgentAlerts.length === 0)

  return (
    <Center
      opacity={closed ? 0 : 1}
      pointerEvents={closed ? 'none' : 'auto'}
      transition="opacity 300ms"
      position="absolute"
      left={0}
      top={0}
      zIndex={2}
      bg="gray.100"
      height="100%"
      width="100%">
      <VStack
        divider={<Divider />}
        bg="white"
        shadow="md"
        borderRadius={6}
        spacing={2}
        py={2}
        px={4}
        maxW="800px">
        <Text fontWeight={600} fontSize="lg">
          Review urgent alerts
        </Text>
        {urgentAlerts.map(alert => (
          <Center key={alert.text} p={1} borderRadius="md" width="100%" flexFlow="column">
            {alert.practice ? (
              <Text fontSize="sm" fontWeight={600} color="gray.500">
                PRACTICE ALERT
              </Text>
            ) : null}
            {alert.admin ? (
              <Text fontSize="sm" fontWeight={600} color="gray.500">
                ADMIN ALERT
              </Text>
            ) : null}
            <Text fontWeight={600} fontSize="lg" color="red.500">
              {alert.text}
            </Text>
            {alert.Render ? (
              <Box w="100%" borderRadius="md" border="1px solid #cdcdcd" mt={2}>
                <alert.Render closeAlertsPopup={() => setClosed(true)} />
              </Box>
            ) : null}
          </Center>
        ))}
        <SolidActionButton onClick={() => setClosed(true)} color="white" bg={colors.green.hex}>
          Continue to profile
        </SolidActionButton>
      </VStack>
    </Center>
  )
}
